/**
 * Entity Count Cards Component
 * Displays entity counts grouped by category in a card grid
 */

import React from 'react';
import { MousePointerClick } from 'lucide-react';

export interface EntityTypeMetadata {
  type: string;
  label: string;
  category: string;
}

export interface EntityCount {
  type: string;
  label: string;
  count: number;
  category: string;
}

interface EntityCountCardsProps {
  entityCounts: EntityCount[];
  onEntityClick: (entityType: string) => void;
  emptyMessage?: string;
}

export function EntityCountCards({ 
  entityCounts, 
  onEntityClick,
  emptyMessage = 'No entities found. Please run ingest jobs first.'
}: EntityCountCardsProps) {
  // Group counts by category
  const groupedCounts = entityCounts.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, EntityCount[]>);

  if (entityCounts.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {Object.entries(groupedCounts).map(([category, entities]) => (
        <div key={category} className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">{category}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {entities.map((entity) => (
              <div
                key={entity.type}
                onClick={() => onEntityClick(entity.type)}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-all cursor-pointer border border-gray-200 dark:border-gray-700 hover:border-blue-500 hover:scale-105"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-600 dark:text-gray-300">{entity.label}</h3>
                  <MousePointerClick className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                </div>
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {entity.count.toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {entity.type}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}











